import { sortBy } from 'lodash';
import { ASSIST_API, requestParams } from 'services/assist-api/axios';
import type { AssistQuery } from 'services/assist-api/query';
import { useQueryAssist } from 'services/assist-api/query-client';

export type BaseLadder = API_JSON & {
  activeFrom: string;
  activeTo: string | null;
  name: string;
  value: number;
  type: string;
  area: API_JSON;
  level: API_JSON;
  test: API_JSON;
};

export type StageLadder = {
  stageName: string;
  upperBound: number;
  lowerBound: number;
  levelId: number;
  type: string;
  ladderName: string;
};

const order = { 'order[order]': 'asc' } as const;
const groups = [];
/**
 * Fetch ladders
 * @param params can be used to customise/limit the ladders retrieved
 * @returns an array of ladders
 */
export const getLadders: GetMany<BaseLadder> = async (params) =>
  (await ASSIST_API.get('ladder-datas', { params: requestParams({ order, groups }, params) })).data;

/**
 * Fetch ladder (singular)
 * @param params can be used to customise/limit the ladders retrieved
 * @returns a single ladder
 */
export const getLadder: GetOne<BaseLadder> = async (id, params) =>
  (await ASSIST_API.get(`ladder-datas/${id}`, { params: requestParams({ order, groups }, params) }))
    .data;

/**
 * Hook implementation to fetch ladders
 *  - caches data
 *  - loading, error, etc
 * @param params additional options to filter the request
 * @returns ladders matching request
 */
export const useLadders: AssistQuery.UseMany<BaseLadder> = (params, options) =>
  useQueryAssist(['ladder-datas', params], () => getLadders(params), {
    staleTime: STALE_LONG,
    ...options
  });

/**
 * Given an ladderId fetch and return the ladder
 *  - only runs once ladderId is provided
 *  - fetches the one ladder
 *  - caches data
 * @param id ladderId
 * @param params optional parameters to change requested data
 * @returns the matching ladder
 */
export const useLadder: AssistQuery.UseOne<BaseLadder> = (id, params, options) =>
  useQueryAssist(['ladder-datas', { id, ...params }], () => getLadder(id, params), {
    staleTime: STALE_LONG,
    ...options,
    enabled: !!id
  });

// Transform the ladders data to be more usable (with upper and lower boundaries)
export const transformLadders = (ladders: BaseLadder[]): StageLadder[] => {
  // Note: the ladder.value uses the upper bound of each stage

  const stageLadders = sortBy(ladders, (ladder) => +ladder.value)
    .map((ladder: BaseLadder, i: number, arr: Array<BaseLadder>) => {
      const stageLadder: StageLadder = {
        stageName: ladder.name,
        upperBound: +ladder.value,
        lowerBound: i === 0 ? 0 : +arr[i - 1].value,
        levelId: ladder.level.id,
        type: ladder.type,
        ladderName: `Year ${ladder.level.id}`
      };
      return stageLadder;
    })
    .filter((ladder) => ladder.stageName !== '0');
  return stageLadders;
};
